import '@rainbow-me/rainbowkit/styles.css';

import { getDefaultConfig, RainbowKitProvider, lightTheme } from '@rainbow-me/rainbowkit';
import { WagmiProvider } from 'wagmi';
import { base } from 'wagmi/chains';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactNode } from 'react';

// Create a dedicated query client for wagmi (separate from app's react-query)
const wagmiQueryClient = new QueryClient();

// Get project ID from environment.
// Fallback placeholder: RainbowKit's getDefaultConfig THROWS on empty projectId,
// which crashes the whole React tree (blank page). Placeholder keeps injected
// wallets (MetaMask etc.) working; WalletConnect relay needs the real ID in prod.
const projectId = import.meta.env.VITE_WALLETCONNECT_PROJECT_ID || 'brite-dev-placeholder';

if (!import.meta.env.VITE_WALLETCONNECT_PROJECT_ID) {
  console.warn(
    '[WalletProvider] VITE_WALLETCONNECT_PROJECT_ID not set — using placeholder. WalletConnect relay disabled; injected wallets still work.',
  );
}

// BRITE runs on Base only (USDC membership payments)
const config = getDefaultConfig({
  appName: 'BRITE',
  projectId,
  chains: [base],
  ssr: false, // Disable SSR since we're client-side only
});

// BRITE light theme — terracotta accent from theme/theme.css palette
const briteTheme = lightTheme({
  accentColor: '#c25a3a', // --brite-terra
  accentColorForeground: '#f4efe8', // --brite-paper
  borderRadius: 'large',
  fontStack: 'system',
  overlayBlur: 'small',
});

interface WalletProviderProps {
  children: ReactNode;
}

export function WalletProvider({ children }: WalletProviderProps) {
  return (
    <WagmiProvider config={config}>
      <QueryClientProvider client={wagmiQueryClient}>
        <RainbowKitProvider theme={briteTheme} modalSize="compact">
          {children}
        </RainbowKitProvider>
      </QueryClientProvider>
    </WagmiProvider>
  );
}

// Re-export wagmi config for use in hooks
export { config as wagmiConfig };
